import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '@/lib/cache'
import { toCamelCase } from '@/lib/typeHelper'
import type { InsuranceApplication } from '@/models/insuranceApplication'

/** The application after a Requote, with staff notices about carriers it left alone. */
export type RequoteResult = InsuranceApplication & { requoteNotices?: string[] }

interface RequoteInput {
  /** New coverage selection. Omit to re-quote the coverages already on file. */
  coverages?: string[]
  /** Send even though Rocklake was dispatched moments ago; the backend otherwise answers 429. */
  force?: boolean
}

/**
 * Sends the application back out to every carrier. Cai staff only — the backend flips the
 * existing quotes back to pending before dispatching, so refetching the application is
 * what turns the cards into their loading state.
 */
export function useRequestRequote(applicationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ coverages, force }: RequoteInput = {}) =>
      axios
        .post(`/api/v1/insure/insurance-application/${applicationId}/requote/`, {
          ...(coverages ? { coverages } : {}),
          ...(force ? { force: true } : {}),
        })
        .then((res) => toCamelCase(res.data) as RequoteResult),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION] })
      // The customer page's changed-since-quote banner reads the application through the org.
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
    },
  })
}

/**
 * Asks the customer's sales owner to look at fresh prices — an email and a Slack ping,
 * nothing sent to a carrier. Whether Cai then re-quotes is their call.
 */
export function useRequestRequoteReview(applicationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      axios
        .post(`/api/v1/insure/insurance-application/${applicationId}/requote-request/`, {})
        .then((res) => toCamelCase(res.data) as InsuranceApplication),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION] }),
  })
}
